import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import storageService from '../../../../utils/storageService';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faClock,
  faUsers,
  faShieldHalved,
  faBuildingUser,
  faSearch,
  faRoute,
  faUserCheck,
  faInfoCircle,
  faCheckCircle,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import CommonTable from '../../table/ComonTable';
import CommonModal from '../../commonmodal/CommonModal';
import DeleteModal from '../../commonmodal/DeleteModal';
import { roundInspectionApi } from '../roundInspectionApi';
import apiServices from '../../../../services/apiService';

const RoundSchedulerList = ({ siteId }) => {
  // Vendor Context Detection
  const { user: authUser } = useSelector((state) => state.auth || {});
  const userRoleId = Number(storageService.getItem("roleId") || authUser?.roleId || localStorage.getItem("roleId") || 0);
  const personTypeId = Number(storageService.getItem("personTypeId") || authUser?.personTypeId || localStorage.getItem("personTypeId") || 0);
  const isVendorLogin = userRoleId === 7 || personTypeId === 4;
  const loggedInVendorId = isVendorLogin
    ? Number(storageService.getItem("vendorId") || authUser?.vendorId || authUser?.id || authUser?.Id || authUser?.userId || storageService.getItem("employeeCode") || localStorage.getItem("vendorId") || 0)
    : null;

  const [rounds, setRounds] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVendorFilter, setSelectedVendorFilter] = useState('');

  // Round Add/Edit Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  // Assign Staff Modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [durationPreset, setDurationPreset] = useState('30days'); // 'today' | '7days' | '30days' | '60days' | 'custom'
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 29);
    return d.toISOString().split('T')[0];
  });
  const [daysFilter, setDaysFilter] = useState('all'); // 'all' | 'weekdays' | 'weekends'
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState([1, 2, 3, 4, 5, 6, 7]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [staffSearch, setStaffSearch] = useState('');

  const applyPreset = (preset) => {
    setDurationPreset(preset);
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);

    if (preset === 'today') {
      setEndDate(todayStr);
    } else if (preset === '7days') {
      const d = new Date();
      d.setDate(d.getDate() + 6);
      setEndDate(d.toISOString().split('T')[0]);
    } else if (preset === '30days') {
      const d = new Date();
      d.setDate(d.getDate() + 29);
      setEndDate(d.toISOString().split('T')[0]);
    } else if (preset === '60days') {
      const d = new Date();
      d.setDate(d.getDate() + 59);
      setEndDate(d.toISOString().split('T')[0]);
    }
  };

  const applyDaysFilter = (filterType) => {
    setDaysFilter(filterType);
    if (filterType === 'all') {
      setSelectedDaysOfWeek([1, 2, 3, 4, 5, 6, 7]);
    } else if (filterType === 'weekdays') {
      setSelectedDaysOfWeek([1, 2, 3, 4, 5]);
    } else if (filterType === 'weekends') {
      setSelectedDaysOfWeek([6, 7]);
    }
  };

  const activeDaysCount = useMemo(() => {
    try {
      const s = new Date(startDate);
      const e = new Date(endDate);
      if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
      let count = 0;
      for (let cur = new Date(s); cur <= e; cur.setDate(cur.getDate() + 1)) {
        const day = cur.getDay() === 0 ? 7 : cur.getDay(); // 1=Mon ... 7=Sun
        if (selectedDaysOfWeek.includes(day)) count++;
      }
      return count;
    } catch {
      return 0;
    }
  }, [startDate, endDate, selectedDaysOfWeek]);

  // Delete Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Form State
  const initialForm = {
    roundName: '',
    shiftName: 'Night Shift',
    vendorId: '',
    startTime: '22:00',
    endTime: '06:00',
    recurrencePattern: 'DAILY',
    gracePeriodMinutes: 30,
    staffScope: 'ALL',
    staffRoleId: 0,
    selectedCheckpoints: [],
    assignedStaffUserIds: [],
  };
  const [formData, setFormData] = useState(initialForm);

  // Load active vendors for Site Admin
  const fetchVendors = useCallback(async () => {
    if (isVendorLogin) return;
    try {
      const res = await apiServices({
        url: `/vendor-management/vendor/GetVendors?siteId=${siteId}&pageSize=100`,
        method: 'GET'
      });
      const list = res?.data?.data || (Array.isArray(res?.data) ? res.data : []);
      setVendors(list);
    } catch (e) {
      console.warn('Failed to fetch vendors:', e);
    }
  }, [siteId, isVendorLogin]);

  // Assignment Cache Helpers
  const getCacheKey = useCallback(
    (dateStr) => `round_assignments_${siteId}_${dateStr || new Date().toISOString().split('T')[0]}`,
    [siteId]
  );

  const loadCachedAssignments = useCallback(() => {
    try {
      const raw = localStorage.getItem(getCacheKey());
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [getCacheKey]);

  const saveCachedAssignments = useCallback(
    (roundId, staffIds, dateStr) => {
      try {
        const key = getCacheKey(dateStr);
        const raw = localStorage.getItem(key);
        const current = raw ? JSON.parse(raw) : {};
        current[String(roundId)] = (staffIds || []).map(Number);
        localStorage.setItem(key, JSON.stringify(current));
      } catch (e) {
        console.warn('Failed to cache assignments', e);
      }
    },
    [getCacheKey]
  );

  // Load rounds, checkpoints, and templates
  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const vendorParam = isVendorLogin
        ? loggedInVendorId
        : (selectedVendorFilter && selectedVendorFilter !== 'IN_HOUSE' ? Number(selectedVendorFilter) : null);
      const [roundsRes, locsRes, tmplRes] = await Promise.all([
        roundInspectionApi.getRounds(siteId, vendorParam),
        roundInspectionApi.getLocations(siteId),
        roundInspectionApi.getTemplates(siteId),
      ]);

      const rawRounds = roundsRes?.data?.data || roundsRes?.data || [];
      const cache = loadCachedAssignments();
      const enrichedRounds = rawRounds.map((r) => {
        const rId = r.roundId || r.id;
        const cachedStaff = cache[String(rId)];
        if ((!r.assignedStaffUserIds || r.assignedStaffUserIds.length === 0) && cachedStaff && cachedStaff.length > 0) {
          return {
            ...r,
            assignedStaffUserIds: cachedStaff,
            assignedStaff: r.assignedStaff?.length > 0
              ? r.assignedStaff
              : cachedStaff.map((sid) => ({ staffUserId: sid, staffName: `Staff #${sid}` })),
          };
        }
        return r;
      });
      setRounds(enrichedRounds);
      setCheckpoints(locsRes?.data?.data || locsRes?.data || []);
      setTemplates(tmplRes?.data?.data || tmplRes?.data || []);
    } catch (err) {
      toast.error('Failed to load scheduler data: ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  }, [siteId, isVendorLogin, loggedInVendorId, selectedVendorFilter]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Open Add Modal
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setEditId(null);
    setFormData(initialForm);
    setIsModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (round) => {
    const targetId = round.roundId || round.id;
    setIsEditMode(true);
    setEditId(targetId);

    const formatTime = (t) => {
      if (!t) return '00:00';
      if (typeof t === 'string' && t.includes(':')) {
        return t.substring(0, 5);
      }
      return '00:00';
    };

    const cache = loadCachedAssignments();
    const preselectedStaff = (round.assignedStaffUserIds && round.assignedStaffUserIds.length > 0)
      ? round.assignedStaffUserIds
      : (Array.isArray(round.assignedStaff) && round.assignedStaff.length > 0)
        ? round.assignedStaff.map((s) => s.staffUserId || s.id)
        : (cache[String(targetId)] || []);
    setFormData({
      roundName: round.roundName || '',
      shiftName: round.shiftName || 'Night Shift',
      vendorId: round.vendorId || '',
      startTime: formatTime(round.expectedWindowStart || round.startTime),
      endTime: formatTime(round.expectedWindowEnd || round.endTime),
      recurrencePattern: round.frequency || round.recurrencePattern || 'DAILY',
      gracePeriodMinutes: round.gracePeriodMinutes || 30,
      staffScope: round.vendorId ? 'VENDOR' : (round.staffScope || 'ALL'),
      staffRoleId: round.staffRoleId || 0,
      selectedCheckpoints: Array.isArray(round.checkpoints)
        ? round.checkpoints.map((c, idx) => ({
            locationId: c.locationId || c.id,
            templateId: c.templateId || (templates[0]?.templateId || templates[0]?.id || 0),
            orderIndex: c.sequenceOrder || c.orderIndex || idx + 1,
          }))
        : [],
      assignedStaffUserIds: preselectedStaff,
    });
    setIsModalOpen(true);
  };

  // Checkpoint selection toggle
  const toggleCheckpoint = (locId) => {
    setFormData((prev) => {
      const exists = prev.selectedCheckpoints.find((c) => c.locationId === locId);
      if (exists) {
        return {
          ...prev,
          selectedCheckpoints: prev.selectedCheckpoints.filter(
            (c) => c.locationId !== locId
          ),
        };
      } else {
        const nextOrder = prev.selectedCheckpoints.length + 1;
        const defaultTmpl = templates[0]?.templateId || templates[0]?.id || 0;
        return {
          ...prev,
          selectedCheckpoints: [
            ...prev.selectedCheckpoints,
            {
              locationId: locId,
              templateId: defaultTmpl,
              orderIndex: nextOrder,
            },
          ],
        };
      }
    });
  };

  // Save Round (Site Admin)
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!formData.roundName.trim()) {
      toast.warn('Please provide a Round Name');
      return;
    }

    const roundIdVal = isEditMode ? editId : 0;
    const sTime = formData.startTime.length === 5 ? formData.startTime + ':00' : formData.startTime;
    const eTime = formData.endTime.length === 5 ? formData.endTime + ':00' : formData.endTime;
    const chosenVendorId = formData.vendorId ? Number(formData.vendorId) : null;

    const payload = {
      id: roundIdVal,
      roundId: roundIdVal,
      siteId: Number(siteId),
      roundName: formData.roundName.trim(),
      shiftName: formData.shiftName.trim(),
      vendorId: chosenVendorId,
      staffScope: chosenVendorId ? 'VENDOR' : 'IN_HOUSE',
      startTime: sTime,
      expectedWindowStart: sTime,
      endTime: eTime,
      expectedWindowEnd: eTime,
      frequency: formData.recurrencePattern || 'DAILY',
      recurrencePattern: formData.recurrencePattern || 'DAILY',
      gracePeriodMinutes: parseInt(formData.gracePeriodMinutes, 10) || 30,
      staffRoleId: parseInt(formData.staffRoleId, 10) || 0,
      isActive: true,
      isActiveId: 1,
      checkpoints: formData.selectedCheckpoints.map((c, idx) => ({
        id: c.id || 0,
        roundId: roundIdVal,
        locationId: c.locationId,
        templateId: Number(c.templateId) || (templates[0]?.templateId || templates[0]?.id || 1),
        sequenceOrder: idx + 1,
        orderIndex: idx + 1,
        isMandatory: true,
      })),
    };

    try {
      if (isEditMode) {
        await roundInspectionApi.updateRound(editId, payload);
        if (formData.assignedStaffUserIds && formData.assignedStaffUserIds.length > 0) {
          const staffNums = formData.assignedStaffUserIds.map(Number);
          await roundInspectionApi.assignStaff({
            roundId: editId,
            staffUserIds: staffNums,
            assignedDate: new Date().toISOString().split('T')[0],
          });
          saveCachedAssignments(editId, staffNums);
        }
        toast.success('Patrol round schedule updated');
      } else {
        const createRes = await roundInspectionApi.createRound(payload);
        const createdId = createRes?.data?.data || createRes?.data;
        if (createdId && formData.assignedStaffUserIds?.length > 0) {
          const staffNums = formData.assignedStaffUserIds.map(Number);
          await roundInspectionApi.assignStaff({
            roundId: Number(createdId),
            staffUserIds: staffNums,
            assignedDate: new Date().toISOString().split('T')[0],
          });
          saveCachedAssignments(createdId, staffNums);
        }
        toast.success('Patrol round schedule created');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save round: ' + (err.response?.data?.message || err.message));
    }
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      await roundInspectionApi.deleteRound(itemToDelete.roundId || itemToDelete.id);
      toast.success('Patrol round schedule deactivated');
      setDeleteModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete: ' + (err.message || ''));
    } finally {
      setIsDeleting(false);
    }
  };

  // Open Assign Modal (Loads vendor-specific staff)
  const handleOpenAssign = async (round) => {
    const vendorObj = vendors.find((v) => String(v.vendorId || v.id) === String(round.vendorId));
    const resolvedVendorName = round.vendorName || vendorObj?.vendorName || (round.vendorId ? `Vendor #${round.vendorId}` : null);
    const resolvedRound = {
      ...round,
      vendorName: resolvedVendorName,
    };
    setSelectedRound(resolvedRound);
    const targetRoundId = round.roundId || round.id;
    const cache = loadCachedAssignments();
    const currentlyAssigned = (round.assignedStaffUserIds && round.assignedStaffUserIds.length > 0)
      ? round.assignedStaffUserIds
      : (Array.isArray(round.assignedStaff) && round.assignedStaff.length > 0)
        ? round.assignedStaff.map((s) => s.staffUserId || s.id)
        : (cache[String(targetRoundId)] || []);
    setSelectedStaffIds(currentlyAssigned.map(Number));
    setStaffSearch('');
    applyPreset('30days');
    setAssignModalOpen(true);
    setLoadingStaff(true);

    try {
      const targetVendorId = round.vendorId || (isVendorLogin ? loggedInVendorId : null);
      let staffUrl = `/staff-management/staff/GetStaff?siteId=${siteId}&pageSize=100`;
      if (targetVendorId) {
        staffUrl += `&vendorId=${targetVendorId}`;
      }
      const res = await apiServices({ url: staffUrl, method: 'GET' });
      const rawList = res?.data?.data || (Array.isArray(res?.data) ? res.data : []);
      const mapped = rawList.map((s) => ({
        id: s.staffId || s.id,
        name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || `Staff #${s.staffId || s.id}`,
        mobile: s.mobileNumber || s.phone || '',
        role: s.staffRoleName || s.roleName || (resolvedVendorName ? `${resolvedVendorName} Guard` : 'Security Guard'),
      }));
      setAvailableStaff(mapped);
    } catch (e) {
      console.warn('Failed to load available staff for round assignment:', e);
      setAvailableStaff([]);
    } finally {
      setLoadingStaff(false);
    }
  };

  // Submit Staff Assignment
  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStaffIds || selectedStaffIds.length === 0) {
      toast.warn('Please select at least one staff officer / guard');
      return;
    }
    try {
      const targetRoundId = selectedRound.roundId || selectedRound.id;
      const staffNums = selectedStaffIds.map(Number);
      const isSingle = durationPreset === 'today';

      await roundInspectionApi.assignStaff({
        roundId: targetRoundId,
        staffUserIds: staffNums,
        assignedDate: startDate,
        startDate: startDate,
        endDate: isSingle ? startDate : endDate,
        daysOfWeek: (isSingle || selectedDaysOfWeek.length === 7) ? null : selectedDaysOfWeek,
      });

      // Save cache immediately
      saveCachedAssignments(targetRoundId, staffNums, startDate);

      // Immediately update local rounds state
      setRounds((prevRounds) =>
        prevRounds.map((r) => {
          if ((r.roundId || r.id) === targetRoundId) {
            const mappedStaff = availableStaff
              .filter((stf) => staffNums.some((id) => String(id) === String(stf.id)))
              .map((stf) => ({
                staffUserId: Number(stf.id),
                staffName: stf.name,
                roleName: stf.role,
                mobileNumber: stf.mobile,
              }));
            return {
              ...r,
              assignedStaffUserIds: staffNums,
              assignedStaff: mappedStaff.length > 0
                ? mappedStaff
                : staffNums.map((sid) => ({ staffUserId: sid, staffName: `Staff #${sid}` })),
            };
          }
          return r;
        })
      );

      toast.success(
        isSingle
          ? `Round successfully assigned to ${selectedStaffIds.length} guard(s) for ${startDate}!`
          : `Successfully scheduled ${selectedStaffIds.length} guard(s) across ${activeDaysCount} tour day(s)!`
      );
      setAssignModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to assign staff: ' + (err.response?.data?.message || err.message));
    }
  };

  const toggleStaffSelection = (staffId) => {
    setSelectedStaffIds((prev) => {
      const exists = prev.some((id) => String(id) === String(staffId));
      return exists
        ? prev.filter((id) => String(id) !== String(staffId))
        : [...prev, Number(staffId)];
    });
  };

  const toggleSelectAllStaff = () => {
    if (selectedStaffIds.length === filteredStaffList.length) {
      setSelectedStaffIds([]);
    } else {
      setSelectedStaffIds(filteredStaffList.map((s) => s.id));
    }
  };

  const filteredStaffList = availableStaff.filter((s) =>
    s.name.toLowerCase().includes(staffSearch.toLowerCase()) ||
    s.mobile.includes(staffSearch) ||
    s.role.toLowerCase().includes(staffSearch.toLowerCase())
  );

  const filteredRounds = rounds.filter((r) => {
    // 1. Vendor filtering
    if (selectedVendorFilter === 'IN_HOUSE') {
      if (r.vendorId) return false;
    } else if (selectedVendorFilter) {
      if (String(r.vendorId) !== String(selectedVendorFilter)) return false;
    }

    // 2. Search term filtering
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    const vendorObj = vendors.find((v) => String(v.vendorId || v.id) === String(r.vendorId));
    const effectiveVendorName = r.vendorName || vendorObj?.vendorName || (r.vendorId ? `Vendor #${r.vendorId}` : 'In-House');

    return (
      (r.roundName || '').toLowerCase().includes(term) ||
      (r.shiftName || '').toLowerCase().includes(term) ||
      effectiveVendorName.toLowerCase().includes(term)
    );
  });

  // Table Configuration matching project design standard
  const tableHeaders = [
    { label: 'PATROL ROUND', key: 'roundName', align: 'left' },
    { label: 'ASSIGNED VENDOR', key: 'vendorName', align: 'center' },
    { label: 'TIMING / WINDOW', key: 'shiftTiming', align: 'center' },
    { label: 'CHECKPOINTS', key: 'checkpointCount', align: 'center' },
    { label: 'ASSIGNED GUARDS', key: 'assignedStaff', align: 'center' },
    { label: 'STATUS', key: 'status', align: 'center' },
    { label: 'ACTIONS', key: 'actions', align: 'center' },
  ];

  const renderCell = (key, row) => {
    switch (key) {
      case 'roundName':
        return (
          <div className="text-left">
            <div className="font-bold text-xs text-textprimary flex items-center gap-1.5">
              <FontAwesomeIcon icon={faRoute} className="text-indigo-600 text-[11px]" />
              {row.roundName}
            </div>
            <div className="text-[10px] text-textsecondary mt-0.5">
              {row.shiftName || 'Standard Shift'}
              {row.isStrictSequence && (
                <span className="ml-1.5 px-1.5 py-0.2 bg-amber-50 text-amber-700 text-[9px] rounded font-bold border border-amber-200">
                  Strict Order
                </span>
              )}
            </div>
          </div>
        );
      case 'vendorName': {
        const vendorObj = vendors.find((v) => String(v.vendorId || v.id) === String(row.vendorId));
        const effectiveName = row.vendorName || vendorObj?.vendorName || (row.vendorId ? `Vendor #${row.vendorId}` : null);
        return effectiveName ? (
          <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faBuildingUser} className="text-[10px]" />
            {effectiveName}
          </span>
        ) : (
          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 border border-gray-200">
            In-House
          </span>
        );
      }
      case 'shiftTiming': {
        const start = row.expectedWindowStart || row.startTime || '00:00';
        const end = row.expectedWindowEnd || row.endTime || '00:00';
        const fmt = (t) => String(t).substring(0, 5);
        return (
          <div className="text-center font-mono text-xs text-textprimary">
            {fmt(start)} - {fmt(end)}
            <div className="text-[10px] text-textsecondary font-sans">
              +{row.gracePeriodMinutes || 15}m grace
            </div>
          </div>
        );
      }
      case 'recurrence':
        return (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
            {row.frequency || row.recurrencePattern || 'DAILY'}
          </span>
        );
      case 'checkpointCount': {
        const count = Array.isArray(row.checkpoints) ? row.checkpoints.length : 0;
        return (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-textprimary">
            <span className="w-5 h-5 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center text-[10px] font-bold">
              {count}
            </span>
            Stops
          </span>
        );
      }
      case 'assignedStaff': {
        const staffList = row.assignedStaff || [];
        if (staffList.length === 0) {
          return (
            <button
              onClick={() => handleOpenAssign(row)}
              className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              title="Click to assign guards"
            >
              <FontAwesomeIcon icon={faUserCheck} />
              Assign Guards
            </button>
          );
        }
        return (
          <div className="flex flex-col items-center gap-0.5">
            <button
              onClick={() => handleOpenAssign(row)}
              className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors inline-flex items-center gap-1.5 cursor-pointer shadow-xs"
              title="Click to change or view assigned guards"
            >
              <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
              {staffList.length} {staffList.length === 1 ? 'Guard' : 'Guards'}
            </button>
            <div
              className="text-[10px] text-textsecondary truncate max-w-[140px] font-medium"
              title={staffList.map((s) => s.staffName || ('Staff #' + (s.staffUserId || s.id))).join(', ')}
            >
              {staffList.map((s) => s.staffName || ('Staff #' + (s.staffUserId || s.id))).join(', ')}
            </div>
          </div>
        );
      }
      case 'status': {
        const isActive = row.isActiveId === 1 || row.isActive === true;
        return isActive ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
            Active
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200">
            Inactive
          </span>
        );
      }
      default:
        return row[key] || '-';
    }
  };

  const headerActions = (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Vendor Filter (Site Admin Only) */}
      {!isVendorLogin && (
        <div className="flex items-center gap-1.5 bg-white dark:bg-cardbg border border-gray-200 dark:border-bordercolor rounded-lg px-2.5 py-1.5 shadow-sm text-xs">
          <FontAwesomeIcon icon={faFilter} className="text-textsecondary text-xs" />
          <select
            value={selectedVendorFilter}
            onChange={(e) => setSelectedVendorFilter(e.target.value)}
            className="bg-transparent border-none text-xs font-medium text-textprimary focus:outline-none cursor-pointer"
          >
            <option value="">All Vendors & In-House</option>
            <option value="IN_HOUSE">In-House Only</option>
            {vendors.map((v) => (
              <option key={v.vendorId || v.id} value={v.vendorId || v.id}>
                {v.vendorName || v.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Search Bar */}
      <div className="relative">
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
        />
        <input
          type="text"
          placeholder={isVendorLogin ? "Search assigned rounds..." : "Search rounds, vendors..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-bordercolor bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500 w-44 sm:w-56"
        />
      </div>

      {/* Create Button (Site Admin Only) */}
      {!isVendorLogin && (
        <button
          onClick={handleOpenAdd}
          className="px-3.5 py-1.5 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm inline-flex items-center gap-1.5"
        >
          <FontAwesomeIcon icon={faPlus} />
          Create Patrol Round
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Subtitle / Role Banner */}
      {isVendorLogin && (
        <div className="p-3 bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 rounded-xl flex items-center justify-between text-xs text-purple-900 dark:text-purple-300">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faBuildingUser} className="text-purple-600" />
            <span>
              <strong>Vendor Portal:</strong> You are viewing patrol rounds assigned to your agency. Click <strong>Action (⋮) ➔ Assign Staff</strong> to assign your guards to daily shifts.
            </span>
          </div>
        </div>
      )}

      {/* Table Component */}
      <div className="border border-gray-200 dark:border-bordercolor rounded-xl overflow-hidden shadow-sm">
        <CommonTable
          loading={loading}
          tableHeading={isVendorLogin ? "My Assigned Patrol Rounds" : "Patrol Rounds & Shift Schedules"}
          headers={tableHeaders}
          data={filteredRounds}
          renderCell={renderCell}
          headerAction={headerActions}
          onEdit={handleOpenEdit}
          onDelete={!isVendorLogin ? ((row) => {
            setItemToDelete(row);
            setDeleteModalOpen(true);
          }) : null}
          customActions={[
            {
              label: 'Assign Staff',
              icon: faUserCheck,
              onClick: (row) => handleOpenAssign(row),
            },
          ]}
        />
      </div>

      {/* Add / Edit Round Modal using CommonModal (Site Admin & Vendor) */}
      {isModalOpen && (
        <CommonModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={isEditMode ? 'Edit Patrol Round' : 'Create Patrol Round'}
          subTitle="Set up shift timings, assign vendor agency, and configure sequential checkpoint routes."
          footerContent={
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitForm}
                className="px-4 py-2 text-xs font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm"
              >
                {isEditMode ? 'Save Changes' : 'Create Round'}
              </button>
            </div>
          }
        >
          <form onSubmit={handleSubmitForm} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-textprimary mb-1 block">
                  Round Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Night Perimeter Security Patrol"
                  value={formData.roundName}
                  onChange={(e) => setFormData((p) => ({ ...p, roundName: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-textprimary mb-1 block">
                  Assigned Vendor / Agency
                </label>
                {isVendorLogin ? (
                  <div className="w-full border border-gray-200 dark:border-bordercolor rounded-lg p-2 text-xs bg-gray-50 dark:bg-cardbg text-purple-700 font-semibold flex items-center gap-1.5">
                    <FontAwesomeIcon icon={faBuildingUser} />
                    {selectedRound?.vendorName || (loggedInVendorId ? `Vendor #${loggedInVendorId}` : 'My Agency')}
                  </div>
                ) : (
                  <select
                    value={formData.vendorId || ''}
                    onChange={(e) => setFormData((p) => ({ ...p, vendorId: e.target.value ? Number(e.target.value) : '' }))}
                    className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    <option value="">In-House / Direct Staff</option>
                    {vendors.map((v) => (
                      <option key={v.vendorId || v.id} value={v.vendorId || v.id}>
                        {v.vendorName || v.name} ({v.categoryName || v.vendorType || 'Vendor'})
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-[10px] text-textsecondary mt-0.5">
                  If selected, this vendor agency will assign their own guards to this round.
                </p>
              </div>

              <div>
                <label className="text-xs font-semibold text-textprimary mb-1 block">
                  Shift Description
                </label>
                <input
                  type="text"
                  placeholder="e.g. Morning Shift, Night Tour"
                  value={formData.shiftName}
                  onChange={(e) => setFormData((p) => ({ ...p, shiftName: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-textprimary mb-1 block">
                  Recurrence
                </label>
                <select
                  value={formData.recurrencePattern}
                  onChange={(e) => setFormData((p) => ({ ...p, recurrencePattern: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="DAILY">Daily Recurring</option>
                  <option value="WEEKLY">Weekly Recurring</option>
                  <option value="SHIFT_BASED">Shift-Based (Per Rostering)</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-textprimary mb-1 block">
                  Expected Window Start
                </label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData((p) => ({ ...p, startTime: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-textprimary mb-1 block">
                  Expected Window End
                </label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData((p) => ({ ...p, endTime: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Assigned Staff Preview */}
            <div className="p-3 bg-indigo-50/50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900 rounded-xl flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold text-textprimary block">
                  Assigned Guards / Staff ({formData.assignedStaffUserIds?.length || 0})
                </span>
                <span className="text-[11px] text-textsecondary">
                  {formData.assignedStaffUserIds?.length > 0
                    ? `${formData.assignedStaffUserIds.length} guard(s) allocated for daily duty.`
                    : 'No staff currently assigned to this round.'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const targetRound = rounds.find((r) => (r.roundId || r.id) === editId) || { id: editId, roundName: formData.roundName, vendorId: formData.vendorId };
                  handleOpenAssign(targetRound);
                }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors inline-flex items-center gap-1.5"
              >
                <FontAwesomeIcon icon={faUserCheck} />
                Manage Staff Assignment
              </button>
            </div>

            {/* Checkpoint Route Selection */}
            <div>
              <span className="text-xs font-semibold text-textprimary block mb-1">
                Checkpoints in Patrol Route ({formData.selectedCheckpoints.length})
              </span>

              <div className="border border-gray-200 dark:border-bordercolor rounded-xl bg-white dark:bg-cardbg p-2 max-h-[30vh] overflow-y-auto space-y-1.5">
                {checkpoints.length === 0 ? (
                  <div className="text-xs text-textsecondary p-2">
                    No checkpoints found. Please create checkpoints first.
                  </div>
                ) : (
                  checkpoints.map((cp) => {
                    const isSelected = formData.selectedCheckpoints.some(
                      (c) => c.locationId === cp.locationId
                    );
                    const entry = formData.selectedCheckpoints.find(
                      (c) => c.locationId === cp.locationId
                    );
                    return (
                      <div
                        key={cp.locationId}
                        className={`p-2.5 rounded-lg border flex items-center justify-between gap-3 text-xs transition-colors ${
                          isSelected
                            ? 'bg-indigo-50/70 border-indigo-200 dark:bg-indigo-950/30 dark:border-indigo-800'
                            : 'bg-white dark:bg-cardbg border-gray-100 dark:border-bordercolor'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`cp_${cp.locationId}`}
                            checked={isSelected}
                            onChange={() => toggleCheckpoint(cp.locationId)}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
                          />
                          <label htmlFor={`cp_${cp.locationId}`} className="cursor-pointer font-medium text-textprimary">
                            <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 font-mono text-[10px] font-bold text-slate-800 dark:text-slate-200 mr-2">
                              {cp.locationCode}
                            </span>
                            {cp.locationName}
                          </label>
                        </div>

                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] text-textsecondary">Checklist:</span>
                            <select
                              value={entry?.templateId || ''}
                              onChange={(e) => {
                                const newTmpl = e.target.value;
                                setFormData((prev) => ({
                                  ...prev,
                                  selectedCheckpoints: prev.selectedCheckpoints.map((sc) =>
                                    sc.locationId === cp.locationId ? { ...sc, templateId: newTmpl } : sc
                                  ),
                                }));
                              }}
                              className="border border-gray-300 dark:border-bordercolor rounded-lg p-1.5 text-xs bg-white dark:bg-cardbg text-textprimary"
                            >
                              {templates.map((t) => (
                                <option key={t.templateId} value={t.templateId}>
                                  {t.templateName}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </form>
        </CommonModal>
      )}

      {/* Simplified Staff Assignment Modal (For Vendor or Admin) */}
      <CommonModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title="Assign Staff to Patrol Round"
        subTitle={
          selectedRound?.vendorName
            ? `Select ${selectedRound.vendorName} guards/technicians to assign to this tour.`
            : (selectedRound?.vendorId
                ? `Select Vendor #${selectedRound.vendorId} guards/technicians to assign to this tour.`
                : "Select staff officers to execute this patrol round.")
        }
        footerContent={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setAssignModalOpen(false)}
              className="px-4 py-2 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignSubmit}
              disabled={selectedStaffIds.length === 0 || activeDaysCount === 0}
              className={`px-4 py-2 text-xs font-semibold rounded-lg text-white transition-colors shadow-sm ${
                selectedStaffIds.length > 0 && activeDaysCount > 0
                  ? 'bg-indigo-600 hover:bg-indigo-700'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {selectedStaffIds.length > 0
                ? (durationPreset === 'today'
                    ? `Assign ${selectedStaffIds.length} Guard(s) for Today`
                    : `Schedule ${selectedStaffIds.length} Guard(s) for ${activeDaysCount} Days`)
                : 'Select Staff to Schedule'}
            </button>
          </div>
        }
      >
        {selectedRound && (
          <form onSubmit={handleAssignSubmit} className="space-y-4">
            {/* Round Summary Card */}
            <div className="p-3 bg-gray-50 dark:bg-dashboardbg rounded-xl border border-gray-200 dark:border-bordercolor flex items-center justify-between">
              <div>
                <div className="font-bold text-xs text-textprimary">{selectedRound.roundName}</div>
                <div className="text-[11px] text-textsecondary mt-0.5">
                  {selectedRound.shiftName} &bull;{' '}
                  {selectedRound.startTime ? String(selectedRound.startTime).substring(0, 5) : '00:00'} to{' '}
                  {selectedRound.endTime ? String(selectedRound.endTime).substring(0, 5) : '23:59'}
                </div>
              </div>
              <div>
                {selectedRound.vendorName ? (
                  <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-purple-100 text-purple-700">
                    {selectedRound.vendorName}
                  </span>
                ) : selectedRound.vendorId ? (
                  <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-purple-100 text-purple-700">
                    Vendor #{selectedRound.vendorId}
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded-md text-[10px] font-semibold bg-gray-200 text-gray-700">
                    In-House
                  </span>
                )}
              </div>
            </div>

            {/* Schedule Duration Preset Selection */}
            <div>
              <label className="text-xs font-semibold text-textprimary mb-1.5 block">
                Patrol Tour Duration <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2.5">
                {[
                  { id: 'today', label: 'Today Only', desc: '1 Day' },
                  { id: '7days', label: 'Next 7 Days', desc: '1 Week' },
                  { id: '30days', label: 'Next 30 Days', desc: '1 Month' },
                  { id: '60days', label: 'Next 60 Days', desc: '2 Months' },
                ].map((p) => {
                  const isCur = durationPreset === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => applyPreset(p.id)}
                      className={`p-2 rounded-xl border text-center transition-all ${
                        isCur
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-950/40 dark:border-indigo-500 dark:text-indigo-300 font-bold shadow-xs'
                          : 'border-gray-200 dark:border-bordercolor bg-white dark:bg-cardbg text-textsecondary hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-xs">{p.label}</div>
                      <div className="text-[10px] opacity-75">{p.desc}</div>
                    </button>
                  );
                })}
              </div>

              {/* Start & End Date Pickers */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-gray-50/70 dark:bg-dashboardbg rounded-xl border border-gray-200 dark:border-bordercolor">
                <div>
                  <label className="text-[11px] font-semibold text-textsecondary mb-1 block">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setDurationPreset('custom');
                    }}
                    className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-textsecondary mb-1 block">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setDurationPreset('custom');
                    }}
                    disabled={durationPreset === 'today'}
                    className="w-full border border-gray-300 dark:border-bordercolor rounded-lg p-2 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Active Days Filter if multi-day */}
            {durationPreset !== 'today' && (
              <div>
                <label className="text-xs font-semibold text-textprimary mb-1 block">
                  Active Shift Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'all', label: 'All 7 Days (Daily)' },
                    { id: 'weekdays', label: 'Weekdays (Mon-Fri)' },
                    { id: 'weekends', label: 'Weekends (Sat-Sun)' },
                  ].map((df) => (
                    <button
                      key={df.id}
                      type="button"
                      onClick={() => applyDaysFilter(df.id)}
                      className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                        daysFilter === df.id
                          ? 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-700 font-bold'
                          : 'bg-white dark:bg-cardbg text-textsecondary border-gray-200 dark:border-bordercolor hover:bg-gray-50'
                      }`}
                    >
                      {df.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Live Explanation & Schedule Summary Card */}
            <div className="p-3 bg-indigo-50/70 dark:bg-indigo-950/30 rounded-xl border border-indigo-200 dark:border-indigo-800 text-xs text-indigo-950 dark:text-indigo-200 space-y-1">
              <div className="font-bold flex items-center justify-between">
                <span>📅 Schedule Duration:</span>
                <span className="font-mono text-indigo-700 dark:text-indigo-300">
                  {startDate} &rarr; {endDate} ({activeDaysCount} Active Tour Days)
                </span>
              </div>
              <div className="text-[11px] text-indigo-800 dark:text-indigo-300 flex items-center justify-between">
                <span>👮 Staff Assigned:</span>
                <span className="font-semibold">
                  {selectedStaffIds.length > 0 ? `${selectedStaffIds.length} Guard(s) Selected` : 'No guards selected yet'}
                </span>
              </div>
              <div className="text-[11px] text-indigo-600 dark:text-indigo-400 pt-1 border-t border-indigo-200/60 dark:border-indigo-800/60">
                📱 <strong>Automatic Delivery:</strong> Each assigned guard will receive this patrol round on their mobile app every morning without manual daily date changes.
              </div>
            </div>

            {/* Staff Selection List */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-textprimary">
                  Available Guards / Field Staff ({filteredStaffList.length})
                </label>
                {filteredStaffList.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleSelectAllStaff}
                    className="text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold"
                  >
                    {selectedStaffIds.length === filteredStaffList.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>

              {/* Guard Search Input */}
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="Filter staff by name, role, mobile..."
                  value={staffSearch}
                  onChange={(e) => setStaffSearch(e.target.value)}
                  className="w-full border border-gray-200 dark:border-bordercolor rounded-lg px-2.5 py-1.5 text-xs bg-white dark:bg-cardbg text-textprimary focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div className="border border-gray-200 dark:border-bordercolor rounded-xl bg-white dark:bg-cardbg p-2 max-h-[35vh] overflow-y-auto space-y-1.5">
                {loadingStaff ? (
                  <div className="text-center py-6 text-xs text-textsecondary">
                    Loading staff members...
                  </div>
                ) : filteredStaffList.length === 0 ? (
                  <div className="text-center py-6 text-xs text-textsecondary">
                    No staff members found for this vendor.
                    <p className="text-[11px] text-gray-400 mt-1">
                      Ensure staff are registered under Workforce Management with this vendor.
                    </p>
                  </div>
                ) : (
                  filteredStaffList.map((stf) => {
                    const isSelected = selectedStaffIds.some((id) => String(id) === String(stf.id));
                    return (
                      <div
                        key={stf.id}
                        onClick={() => toggleStaffSelection(stf.id)}
                        className={`p-2.5 rounded-lg border flex items-center justify-between gap-3 text-xs cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-indigo-50/80 border-indigo-300 dark:bg-indigo-950/40 dark:border-indigo-700'
                            : 'bg-white dark:bg-cardbg border-gray-100 dark:border-bordercolor hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 pointer-events-none"
                          />
                          <div>
                            <div className="font-semibold text-textprimary">{stf.name}</div>
                            <div className="text-[10px] text-textsecondary">
                              {stf.role} {stf.mobile && `• ${stf.mobile}`}
                            </div>
                          </div>
                        </div>
                        {isSelected && (
                          <FontAwesomeIcon icon={faCheckCircle} className="text-indigo-600 text-sm" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </form>
        )}
      </CommonModal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Deactivate Patrol Round"
        message={`Are you sure you want to deactivate the patrol tour "${itemToDelete?.roundName}"?`}
        loading={isDeleting}
      />
    </div>
  );
};

export default RoundSchedulerList;
